import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { CheckCircle, PlusCircle, Loader2 } from "lucide-react";
import TodoList from "../components/TodoList";
import DashboardStats from "../components/DashboardStats";
import DashboardPipelineFunnel from "../components/DashboardPipelineFunnel";
import DashboardKeyMetrics from "../components/DashboardKeyMetrics";
import SubscriptionThankYouModal from "../components/SubscriptionThankYouModal";
import ErrorBoundary from "../components/ErrorBoundary";
import { recruitingAPI } from "../services/recruitingApi";
import api from "../services/api";
import { getCurrentUser } from "../utils/auth";
import StaleItemsWidget from "../components/StaleItemsWidget";
import "./Dashboard.css";

interface CurrentUser {
  id: number;
  username: string;
  email: string;
  role:
    | "application_admin"
    | "organization_admin"
    | "head_of_recruitment"
    | "head_of_sales"
    | "recruiter"
    | "sales"
    | "tech_checker";
}

interface CandidatesResponse {
  success: boolean;
  data?: unknown[];
  total?: number;
}

interface ApplicationsResponse {
  success: boolean;
  data?: unknown[];
  total?: number;
}

interface JobsResponse {
  success: boolean;
  data?: unknown[];
  total?: number;
}

interface SummaryResponse {
  success: boolean;
  data?: {
    summary: string[] | string;
  };
}

interface CreateTaskData {
  title: string;
  description: string;
  priority: string;
  dueDate: null;
}

interface DashboardInfo {
  title: string;
  subtitle: string;
}

// Normalize text for dedup comparison
function normalizeSummaryText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

// Deduplicate titles by exact match
function uniqueByTitle(items: { title: string }[]): { title: string }[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.title)) return false;
    seen.add(item.title);
    return true;
  });
}

// Filter out AI summary items that duplicate existing tasks or each other
function deduplicateAiSummary(
  summaryItems: string[],
  existingTodos: { title: string }[],
): string[] {
  const todoNorms = new Set(
    existingTodos
      .map((t) => normalizeSummaryText(t.title))
      .filter((n) => n.length > 0),
  );
  const seen = new Set<string>();
  return summaryItems.filter((item) => {
    const norm = normalizeSummaryText(item);
    // Drop if matches an existing task (substring match)
    if ([...todoNorms].some((t) => norm.includes(t) || t.includes(norm)))
      return false;
    // Drop duplicates within the summary itself
    if (seen.has(norm)) return false;
    seen.add(norm);
    return true;
  });
}

const Dashboard: React.FC = () => {
  const { t } = useTranslation(["common"]);
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showAllTasks, setShowAllTasks] = useState(false);
  const [creatingTaskFrom, setCreatingTaskFrom] = useState<string | null>(null);
  const [showThankYouModal, setShowThankYouModal] = useState(false);

  // Get current user from auth utility
  const user = (getCurrentUser() || {}) as CurrentUser;
  const userId = user.id;

  // Check for from_stripe parameter and show thank you modal
  useEffect(() => {
    const fromStripe = searchParams.get("from_stripe");
    if (fromStripe === "true") {
      setShowThankYouModal(true);
      searchParams.delete("from_stripe");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Fetch counts for DashboardStats (these queries also feed the stat cards)
  const { data: candidatesData } = useQuery<CandidatesResponse>(
    ["my-active-candidates", userId],
    () =>
      recruitingAPI.getCandidates({
        has_active_application: "true",
        page: 1,
        limit: 1,
      }) as unknown as Promise<CandidatesResponse>,
    {
      enabled: !!userId,
      staleTime: 30000,
    },
  );

  const { data: applicationsData } = useQuery<ApplicationsResponse>(
    ["my-open-applications", userId],
    () =>
      recruitingAPI.getApplications({
        status: "active",
      }) as unknown as Promise<ApplicationsResponse>,
    {
      enabled: !!userId,
      staleTime: 30000,
    },
  );

  const { data: jobsData } = useQuery<JobsResponse>(
    ["active-jobs", userId],
    () =>
      recruitingAPI.getJobs({
        page: 1,
        pageSize: 1,
      }) as unknown as Promise<JobsResponse>,
    {
      enabled: !!userId,
      staleTime: 30000,
    },
  );

  const candidatesCount =
    candidatesData?.total ?? candidatesData?.data?.length ?? 0;
  // Applications API returns data as a plain array, not paginated envelope
  const applicationsCount =
    applicationsData?.total ??
    applicationsData?.data?.length ??
    (Array.isArray(applicationsData)
      ? (applicationsData as unknown[]).length
      : 0);
  const jobsCount = jobsData?.total ?? jobsData?.data?.length ?? 0;

  // AI Summary state
  const [aiSummary, setAiSummary] = useState<string[] | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Fetch AI Summary with deduplication
  useEffect(() => {
    if (userId) {
      setSummaryLoading(true);
      (
        recruitingAPI.generateSummary(
          userId,
        ) as unknown as Promise<SummaryResponse>
      )
        .then((response) => {
          const rawSummary = response?.data?.summary;
          if (!rawSummary) {
            setAiSummary(null);
            return;
          }
          // Normalize to array
          const items = Array.isArray(rawSummary)
            ? rawSummary
            : rawSummary
                .split("\n")
                .map((s) => s.trim())
                .filter(Boolean);
          // Deduplicate against existing cached todos
          // Use getQueriesData to match any ["todos", ...] key since TodoList uses dynamic filters
          const todosQueries = queryClient.getQueriesData<{
            data: { title: string }[];
          }>(["todos"]);
          const cachedTodos = uniqueByTitle(
            todosQueries.flatMap(([, data]) => data?.data ?? []),
          );
          const deduped = deduplicateAiSummary(items, cachedTodos);
          setAiSummary(deduped.length > 0 ? deduped : null);
        })
        .catch((error) => {
          console.error("Error fetching AI summary:", error);
          setAiSummary(null);
        })
        .finally(() => {
          setSummaryLoading(false);
        });
    }
  }, [userId, queryClient]);

  // Create task from summary item
  const createTaskMutation = useMutation(
    (taskData: CreateTaskData) => api.todo.createTodo(taskData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["todos"]);
        setCreatingTaskFrom(null);
      },
      onError: (error) => {
        console.error("Error creating task:", error);
        alert(t("errors.failedToCreateTask"));
        setCreatingTaskFrom(null);
      },
    },
  );

  const isNonActionableItem = (text: string) =>
    /no specific.*actions needed/i.test(text);

  const handleCreateTaskFromSummary = async (summaryItem: string) => {
    if (isNonActionableItem(summaryItem)) return;
    setCreatingTaskFrom(summaryItem);
    const taskData: CreateTaskData = {
      title:
        summaryItem.length > 100
          ? summaryItem.substring(0, 100) + "..."
          : summaryItem,
      description: summaryItem,
      priority: "medium",
      dueDate: null,
    };
    createTaskMutation.mutate(taskData);
  };

  const getDashboardTitle = (): DashboardInfo => {
    switch (user.role) {
      case "sales":
        return {
          title: t("dashboard.salesDashboard"),
          subtitle: t("dashboard.yourAssignedJobs"),
        };
      case "recruiter":
        return {
          title: t("dashboard.recruiterDashboard"),
          subtitle: t("dashboard.yourAssignedCandidates"),
        };
      case "application_admin":
      case "organization_admin":
      case "head_of_recruitment":
      case "head_of_sales":
        return {
          title: t("dashboard.adminDashboard"),
          subtitle: t("dashboard.overviewAllActivities"),
        };
      default:
        return {
          title: t("dashboard.myDashboard"),
          subtitle: t("dashboard.overviewYourActivities"),
        };
    }
  };

  const dashboardInfo = getDashboardTitle();

  return (
    <div className="dashboard">
      {/* Thank You Modal for successful subscription */}
      <SubscriptionThankYouModal
        isOpen={showThankYouModal}
        onClose={() => setShowThankYouModal(false)}
      />

      <div className="dashboard-header">
        <h1>{dashboardInfo.title}</h1>
        <p>{dashboardInfo.subtitle}</p>
      </div>

      <DashboardStats
        userId={userId}
        candidatesCount={candidatesCount}
        applicationsCount={applicationsCount}
        jobsCount={jobsCount}
      />

      {/* Stale Items Widget (US5) */}
      <StaleItemsWidget />

      {/* AI Summary Section */}
      {(summaryLoading || aiSummary) && (
        <div className="dashboard-section-full">
          <div className="dashboard-section-header">
            <h3>{t("dashboard.aiSummary")}</h3>
          </div>
          <div className="ai-summary-card">
            {summaryLoading && (
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <Loader2 size={16} className="spin" />
                <span>{t("loading.loadingSummary")}</span>
              </div>
            )}
            {!summaryLoading && aiSummary && (
              <ul className="ai-summary-list">
                {aiSummary.map((action) => (
                  <li key={action} className="ai-summary-item">
                    <CheckCircle size={16} className="ai-summary-check" />
                    <span>{action}</span>
                    {!isNonActionableItem(action) && (
                      <button
                        onClick={() => handleCreateTaskFromSummary(action)}
                        disabled={
                          creatingTaskFrom === action ||
                          createTaskMutation.isLoading
                        }
                        className="create-task-btn"
                        title={t("dashboard.createTaskFromItem")}
                      >
                        {creatingTaskFrom === action &&
                        createTaskMutation.isLoading ? (
                          <Loader2 size={16} className="spin" />
                        ) : (
                          <PlusCircle size={16} />
                        )}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Compact Tasks Section */}
      <div className="dashboard-section-full">
        <div className="dashboard-section-header">
          <h3>{t("dashboard.myTasks")}</h3>
          {!showAllTasks && (
            <button
              className="view-all-link"
              onClick={() => {
                setShowAllTasks(true);
                setTimeout(() => {
                  document
                    .getElementById("tasks")
                    ?.scrollIntoView({ behavior: "smooth" });
                }, 100);
              }}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
              }}
            >
              {t("actions.viewAll")} →
            </button>
          )}
          {showAllTasks && (
            <button
              className="view-all-link"
              onClick={() => setShowAllTasks(false)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
              }}
            >
              {t("actions.showLess")} ↑
            </button>
          )}
        </div>
        <div id="tasks">
          <TodoList showFilters={showAllTasks} compact={!showAllTasks} />
        </div>
      </div>

      {/* Pipeline Health Section */}
      <div className="dashboard-bottom">
        <ErrorBoundary>
          <DashboardPipelineFunnel />
        </ErrorBoundary>
        <ErrorBoundary>
          <DashboardKeyMetrics />
        </ErrorBoundary>
      </div>
    </div>
  );
};

export default Dashboard;
